
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import Notification from '@/interfaces/Notification'
import { namespace } from 'vuex-class'
import DOMPurify from 'dompurify'

const Notifications = namespace('notifications')

@Component({})
export default class Snackbar extends Vue {
	@Prop({ type: Boolean, default: false })
	isPush!: boolean

	@Notifications.Action('remove')
	removeNotification

	@Notifications.State('notifications')
	notifications!: Array<Notification>

	visible: boolean = false

	get filteredNotifications() {
		return this.notifications.filter((n) => (this.isPush ? n.isPushMessage : !n.isPushMessage))
	}

	get sanitizedMessage() {
		return DOMPurify.sanitize(this.notification?.message ?? '')
	}

	get notification(): Notification | null {
		return this.filteredNotifications[0] || null
	}

	@Watch('notification')
	onNotificationChange(newVal: Notification | null, oldVal: Notification | null) {
		// Skip if notification didn't actually change (triggered by array mutation)
		if (oldVal && newVal && oldVal.id === newVal.id) {
			return
		}

		if (newVal) {
			// If transitioning from one notification to another
			if (oldVal) {
				// Ensure previous notification is hidden
				this.visible = false
				// Wait for animation to complete before showing next
				setTimeout(() => {
					this.showNotification(newVal)
				}, 350) // Slightly longer than animation duration
			} else {
				// First notification, show immediately
				this.showNotification(newVal)
			}
		}
	}

	showNotification(notification: Notification) {
		// Show notification
		this.visible = true

		// Set timeout for notification duration
		const timeout = notification.timeout || 6000
		setTimeout(() => {
			// Hide notification (start fade-out)
			this.visible = false

			// Wait for fade-out animation to complete
			setTimeout(() => {
				// Remove from store to show next notification
				this.$store.commit('notifications/remove', notification)
			}, 300) // Vuetify default transition duration
		}, timeout)
	}

	close() {
		if (this.notification) {
			this.removeNotification(this.notification)
		}
	}

	navigateToAction() {
		if (!this.notification?.actionURL) return

		try {
			const url = new URL(this.notification.actionURL, globalThis.location.origin)
			const path = url.pathname + url.search
			const v3Route = this.findV3Route(path)

			if (v3Route) {
				globalThis.localStorage.setItem(v3Route + '_last_url', path)

				if (this.isShowingV3Page(v3Route)) {
					const iframe = document.querySelector('[data-testid="v3-app-page"] iframe') as HTMLIFrameElement
					iframe?.contentWindow?.postMessage(`navigate:${path}`, '*')
				} else {
					this.$router.push(v3Route)
				}
			} else {
				this.$router.push(path)
			}
		} catch (e) {
		    // eslint-disable-next-line
			console.error('Failed to navigate to action URL:', e)
		}

		this.close()
	}

	// The V3 page an action URL belongs to. A URL that resolves to a V2 route is judged by that route,
	// so /transports/1971 (detail) and /transports-old stay V2 although they start with /transports.
	// Only a URL without a V2 route (a V3-only path such as /messages/<id>) falls back to the prefix.
	findV3Route(path: string): string | null {
		const { matched } = this.$router.match(path)
		if (matched.length) {
			const record = matched.find((route) => route.meta?.v3Route)
			return (record?.meta.v3Route as string) ?? null
		}

		const route = this.$router.options.routes?.find((route) => {
			const v3Route = route.meta?.v3Route as string | undefined
			if (!v3Route || !path.startsWith(v3Route)) return false
			const rest = path.slice(v3Route.length)
			return rest === '' || /^[/?#]/.test(rest)
		})
		return (route?.meta?.v3Route as string) ?? null
	}

	// Whether the current page is the V3 iframe for that v3Route (not a legacy page under the same path).
	isShowingV3Page(v3Route: string): boolean {
		return this.$route.matched.some((route) => route.meta?.v3Route === v3Route)
	}
}
